import React, { useEffect, useState, useCallback } from "react";
import { Row, Col } from "../../bootstrap/index.jsx";
import Select from "../Select.jsx";
import { update } from "../../../helpers/index.js";
import ColorPicker from "../ColorPicker.jsx";
import TextInput from "../TextInput.jsx";
import HeadingConfiguration from "./HeadingConfiguration.jsx";
import HeaderFooterConfiguration from "./HeaderFooterConfiguration.jsx";
import Button from "../../bootstrap/Button.jsx";
import { FaIcon } from "../Icons.jsx";
import { fetchJson } from "../../../helpers/fetch.js";
import LoadingIndicator from "../loading_indicator.jsx";

import {
  pdf_config_list_designs_path,
  pdf_config_create_design_path
} from '~/routes';

//  TBD: What's chromium headless support?
const FONT_OPTIONS = [
  {label: 'Roboto', value: 'Roboto'},
  {label: 'Optima', value: 'Optima'},
  {label: 'Arial', value: 'Arial'},
  {label: 'Helvetica', value: 'Helvetica'},
  {label: 'Helvetica Neue', value: '"Helvetica Neue"'}
]

export const DEFAULT_DESIGN = {
  header_bg: '#ffffff',
  header_color: '#000000',
  footer_bg: '#ffffff',
  footer_color: '#000000',
  font: 'Roboto',  
  headings: {
    0: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 30, weight: 800, italic: false, underline: true,  upcase: false},
    1: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 36, weight: 200, italic: false, underline: false, upcase: false},
    2: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 30, weight: 200, italic: false, underline: false, upcase: false},
    3: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 22, weight: 400, italic: false, underline: false, upcase: false},
    4: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 18, weight: 400, italic: false, underline: false, upcase: false},
    5: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 14, weight: 400, italic: false, underline: false, upcase: false},
    6: {bg: 'rgba(255,255,255,0)', color: '#000000', size: 12, weight: 400, italic: false, underline: false, upcase: true },
  },
  header: [
    {detail: 'title', l: true,  c: false, r: false},
    {detail: 'page',  l: false, c: false, r: true },
    {detail: 'date',  l: false, c: false, r: false},
    {detail: 'logo',  l: false, c: true,  r: false},
    {detail: 'running_title',  l: false, c: false, r: false},
  ],
  footer: [
    {detail: 'title', l: false, c: false, r: false},
    {detail: 'page',  l: false, c: false, r: false},
    {detail: 'date',  l: false, c: false, r: false},
    {detail: 'logo',  l: false, c: false, r: false},
    {detail: 'running_title',  l: false, c: false, r: false},
  ],
  form_hide_unselected: false,
  form_hide_descriptions: false
}


const DesignConfiguration = ({onConfigChange, mayManageTemplates}) => {
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [modified, setModified] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    onConfigChange({design: design})
  }, [design])

  useEffect(()=>{
    fetchJson(pdf_config_list_designs_path()).then(res => {
      setTemplates(res)
      const defaultTemplate = res.find(t => t.default);
      setTemplate(defaultTemplate?.id || undefined)
      setDesign(defaultTemplate?.content || DEFAULT_DESIGN)
      setLoading(false);
      setModified(false);
    });
  },[])

  const saveAsTemplate = () => {
    // TODO: Loading indicator / disable
    fetchJson(pdf_config_create_design_path(), {
      method: 'POST', 
      body: JSON.stringify({content: design, name: name})
    }).then(res => {
      setTemplates([...templates, {...res, content: design }])
      setTemplate(res.id);
      setModified(false);
    })
  }

  const restoreDefaults = () => {
    const defaultTemplate = templates.find(t => t.default);
    setDesign(defaultTemplate?.content || DEFAULT_DESIGN)
    setTemplate(defaultTemplate?.id || undefined);
    setModified(false);
  }

  const onTemplateChange = useCallback((id) => {
    setTemplate(id)
    setDesign(templates.find(t => t.id == id).content);
    setModified(false);
  }, [templates])

  const updateDesign = (field, value) => {
    setDesign(prevDesign => update(prevDesign, {
      [field]: {
        $set: value,
      },
    }));
    setModified(true);
  }

  const updateCell = useCallback((field, row, col, value) => { 
    setDesign(prevDesign => update(prevDesign, {
      [field]: {
        [row]: {
          [col]: {
            $set: value
          }
        }
      },
    }))
    setModified(true);
  }, [])

  // Allow 1 radio per row & col
  const updateRadioCell = useCallback((field, row, col, value) => {
    let nData = JSON.parse(JSON.stringify(design[field]))
    nData.forEach((_,idx) => {
      nData[idx][col] = false
    })
    nData[row] = {...design[field][row], l: false, c: false, r: false, [col]: value}

    setDesign(prevDesign => update(prevDesign, {
      [field]: {
        $set: nData
      },
    }))
    setModified(true);
  }, [])

  if(loading) return <LoadingIndicator />

  return <>
    <Row className='mb-2 mt-2'>
      <Col size='xs-12'>
        {modified && mayManageTemplates && 
          <Button type='primary' className='pull-right' onClick={saveAsTemplate}>
            <FaIcon type='plus-circle' className='mr-1'/>{I18n.t('save_as_template')}
          </Button>
        }
        <Button type='default' className='pull-right mr-2' onClick={restoreDefaults}>
          <FaIcon type='rotate-left' className='mr-1'/>{I18n.t('restore_defaults')}
        </Button>
      </Col>
      <Col size='xs-6'>
        <label>{I18n.t('template')}</label>
        <Select
          options={[
            ...templates.map(t => ({label: t.name, value: t.id})), 
            ...(modified ? [{label: 'Custom', value: 'new'} ] : [])
          ]}
          value={modified ? 'new' : template}
          onChange={onTemplateChange}
          placeholder={I18n.t('select_design_template')}
        />
      </Col>
      <Col size='xs-6'>
        {modified && mayManageTemplates && <>
          <label>{I18n.t('name')}</label><TextInput onBlur={(e) => setName(e.target.value)} defaultValue={name}/>
        </>}
      </Col>
    </Row>
    <Row className='mb-2 mt-1'>

      <Col size='xs-6' className='mb-1'>
        <label>{I18n.t('document_font')}</label>
        <Select 
          options={FONT_OPTIONS}
          value={design.font}
          onChange={(val) => updateDesign('font', val)}
        />
      </Col>
      <Col size='xs-12' className='mt-2 mb-1'>
        <label>{I18n.t('headings_and_titles')}</label>
        <div className="mr-1 ml-1">
          <HeadingConfiguration headings={design.headings} updateCell={updateCell}/>
        </div>
      </Col>
      <Col size='xs-12' className='mt-2 mb-1'>
        <label>{I18n.t('header_and_footer_design')}</label>
        <Row>
          <Col size='xs-6'>
            <Row>
              <Col size='xs-6'>
                <ColorPicker
                  value={design.header_bg}
                  onChange={val => updateDesign('header_bg', val)}
                />
              </Col>
              <Col size='xs-6'>
                <span>{I18n.t('header_bg_color')}</span>
              </Col>
            </Row>
            <Row className='mt-1'>
              <Col size='xs-6'>
                <ColorPicker
                  value={design.header_color}
                  onChange={val => updateDesign('header_color', val)}
                  />
              </Col>
              <Col size='xs-6'>
                <span>{I18n.t('header_text_color')}</span>
              </Col>
            </Row>
          </Col>
          <Col size='xs-6'>
            <Row>
              <Col size='xs-6'>
                <ColorPicker
                  value={design.footer_bg}
                  onChange={val => updateDesign('footer_bg', val)}
                />
              </Col>
              <Col size='xs-6'>
                <span>{I18n.t('footer_bg_color')}</span>
              </Col>
            </Row>
            <Row className='mt-1'>
              <Col size='xs-6'>
                <ColorPicker
                  value={design.footer_color}
                  onChange={val => updateDesign('footer_color', val)}
                  />
              </Col>
              <Col size='xs-6'>
                <span>{I18n.t('footer_text_color')}</span>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      <Col size='xs-6' className='mt-2'>
        <div className="mr-1 ml-1">
          <HeaderFooterConfiguration data={design.header} type='header' updateCell={updateRadioCell}/>
        </div>
      </Col>
      <Col size='xs-6' className='mt-2'>
        <div className="mr-1 ml-1">
          <HeaderFooterConfiguration data={design.footer} type='footer' updateCell={updateRadioCell}/>
        </div>
      </Col>
      <Col size='xs-12' className='mt-2 mb-1'>
        <label>{I18n.t('form_fields')}</label>
        <Row>
          <Col>
          <input 
            type="checkbox" 
            value={design.hide_unselected}
            id='hide-unselected'
            onChange={e => updateDesign('form_hide_unselected', e.target.checked)}
            />
          <label htmlFor="hide-unselected" className="radio-inline-label">
            {I18n.t("hide_unselected")}
          </label>
          </Col>
        </Row>
        <Row>
          <Col>
            <input 
              type="checkbox" 
              value={design.hide_descriptions}
              id='hide-description'
              onChange={e => updateDesign('form_hide_descriptions', e.target.checked)}
              />
            <label htmlFor="hide-description" className="radio-inline-label">
              {I18n.t("hide_description")}
            </label>
          </Col>
        </Row>
      </Col>
    </Row>
  </>
}


export default DesignConfiguration;


